import {
  ExploreBoxItemEntity,
  ExploreProfileItemEntity,
  ExploreProgramBundleItemEntity,
  ExploreProgramItemEntity,
  ExploreProProgramItemEntity,
} from "@modules/explore/entities/exploreEntity"
import { ExploreFilter } from "@modules/explore/exploreTypes"
import { ProfileAvatar } from "@modules/profile/common/components/profileAvatar"

import { capitalizeCityName } from "@modules/utils/stringUtils"
import { countries, getEmojiFlag, type TCountryCode } from "countries-list"
import { useMemo } from "react"
import { Text } from "@/components/ui/baseUi/text"

type ExploreCardFooterAvatarProps = {
  item:
    | ExploreProgramItemEntity
    | ExploreProgramBundleItemEntity
    | ExploreBoxItemEntity
    | ExploreProfileItemEntity
    | ExploreProProgramItemEntity
} & React.ComponentProps<"div">

export function ExploreCardFooterAvatar({ item, ...props }: ExploreCardFooterAvatarProps) {
  const itemAvatar = useMemo(() => {
    switch (item._type) {
      case ExploreFilter.PROGRAM:
      case ExploreFilter.PROGRAM_PRO:
      case ExploreFilter.PROGRAM_BUNDLE:
        return { avatar: item.owner?.avatar ?? undefined, username: item.owner?.firstname ?? "" }
      case ExploreFilter.BOX:
        return { avatar: item.logo, username: item.name || "" }
      case ExploreFilter.PROFILE:
        return { avatar: item.avatar, username: item.username || "" }
    }
  }, [item])

  const country = useMemo(() => {
    if (item instanceof ExploreBoxItemEntity) {
      const foundCountry = Object.entries(countries).find(
        ([key, value]) =>
          key === item.address.country ||
          value.name.toLowerCase() === item.address.country.toLowerCase() ||
          value.native.toLowerCase() === item.address.country.toLowerCase(),
      )
      if (foundCountry)
        return {
          code: foundCountry[0],
          ...foundCountry[1],
        }
    }
  }, [item])

  return (
    <div className="flex w-full flex-row items-center gap-2" {...props}>
      {item instanceof ExploreProfileItemEntity ? (
        <div className="mt-1 flex items-center gap-2 overflow-hidden p-2 pr-4">
          <ProfileAvatar
            className="size-9"
            profile={{ avatar: itemAvatar?.avatar ?? null, username: itemAvatar?.username || "" }}
          />

          <div className="flex flex-col overflow-hidden">
            <Text fontStyle="LL" className="mb-1 overflow-hidden text-ellipsis text-textDefault-body">
              {`${item.firstname} ${item.lastname}`}
            </Text>
            <Text fontStyle="BS" className="mb-1 overflow-hidden text-ellipsis text-textDefault-body">
              {`@${item.username}`}
            </Text>
          </div>
        </div>
      ) : (
        <div className="mt-1 flex items-center gap-2 overflow-hidden rounded-full bg-gray-800/60 p-1 pr-4">
          <ProfileAvatar
            className="size-9"
            profile={{ avatar: itemAvatar?.avatar ?? null, username: itemAvatar?.username || "" }}
          />
          {(item instanceof ExploreProgramItemEntity ||
            item instanceof ExploreProgramBundleItemEntity ||
            item instanceof ExploreProProgramItemEntity) && (
            <Text
              fontStyle="LS"
              className="mb-1 overflow-hidden text-ellipsis text-textOnColor-body"
            >{`${item.owner?.firstname} ${item.owner?.lastname}`}</Text>
          )}
          {item instanceof ExploreBoxItemEntity && (
            <div className="flex flex-col gap-1">
              <Text fontStyle="LM" className="m text-textOnColor-body">
                {capitalizeCityName(item.address.city)}
              </Text>
              <Text fontStyle="BS" className="text-textOnColor-body">
                {country
                  ? getEmojiFlag(country.code.toUpperCase() as TCountryCode) + " " + country.name
                  : item.address.country}
              </Text>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
